import { Trans, useTranslation } from "react-i18next";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import {
  AddressTypeEnum,
  OrderDetail,
  PreOrderPaymentOptions,
} from "../../types";
import { palette, spacing } from "../../styles/theme";
import { FlexColBox } from "../../common/components";
import YourContactBox from "../../components/ContactBox/ContactBox";
import { Box, Divider, Typography } from "@mui/material";
import { AddressBox } from "../../components/AddressBox";
import OrdersExpandSectionItems from "../../components/Orders/OrdersExpandSectionItems";

interface UserOrderExpandSectionProps {
  orderDetail: OrderDetail;
}

export const UserOrderExpandSection = ({
  orderDetail,
}: UserOrderExpandSectionProps) => {
  const { t } = useTranslation();
  const { isMobile } = useThemeBreakpoints();

  return (
    <FlexColBox gap={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        sx={{ "&>div": { flex: "1 0 0" } }}
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? "24px" : "0"}
        padding={"16px"}
      >
        <FlexColBox gap={isMobile ? "24px" : "16px"}>
          <YourContactBox userDetails={orderDetail.user} />
          {orderDetail.poNumber && (
            <FlexColBox
              gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
            >
              <Typography variant="textSm" color={palette.grey.primary}>
                {t("order.po_number")}
              </Typography>
              <Typography variant="textRegular">
                {orderDetail.poNumber}
              </Typography>
            </FlexColBox>
          )}
          {orderDetail.paymentOption && (
            <FlexColBox
              gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
            >
              <Typography variant="textSm" color={palette.grey.primary}>
                {t("order.payment_option")}
              </Typography>
              <Typography variant="textRegular">
                <Trans
                  i18nKey={`checkout.payment_options.${
                    orderDetail.paymentOption ?? PreOrderPaymentOptions.NOT_SET
                  }`}
                  components={{
                    1: (
                      <Typography
                        variant="textSm"
                        color={palette.grey.primary}
                      />
                    ),
                  }}
                />
              </Typography>
            </FlexColBox>
          )}
          {orderDetail.notes && (
            <FlexColBox
              gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
            >
              <Typography variant="textSm" color={palette.grey.primary}>
                {t("order.note")}
              </Typography>
              <Typography variant="textRegular">{orderDetail.notes}</Typography>
            </FlexColBox>
          )}
        </FlexColBox>
        <AddressBox
          address={orderDetail.shippingAddress}
          addressType={AddressTypeEnum.SHIPPING}
          title={t("order.delivery")}
          hideCopyButton
        />
        <AddressBox
          address={orderDetail.billingAddress}
          addressType={AddressTypeEnum.BILLING}
          title={t("order.billing")}
          hideCopyButton
        />
      </Box>
      <Divider />
      <OrdersExpandSectionItems orderDetail={orderDetail} />
    </FlexColBox>
  );
};
